import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { reportDownload } from '../Reports/report-utils';

interface UseExportOptions {
    /** Redux thunk action that returns a Blob response */
    exportAction: () => any;
    /** File name for the downloaded file (e.g., 'applications.xlsx') */
    fileName: string;
    /** Success message shown in snackbar */
    successMessage?: string;
    /** Error message shown in snackbar */
    errorMessage?: string;
}

interface UseExportReturn {
    /** Whether the export is currently in progress */
    exportLoading: boolean;
    /** Trigger the export */
    handleExport: () => Promise<void>;
}

const useExport = ({
    exportAction,
    fileName,
    successMessage = 'Downloaded successfully',
    errorMessage = 'Failed to download',
}: UseExportOptions): UseExportReturn => {
    const [exportLoading, setExportLoading] = useState(false);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const handleExport = async () => {
        try {
            setExportLoading(true);
            const res = await (dispatch(exportAction()) as unknown as Promise<any>);
            if (res?.type) {
                reportDownload(res as unknown as Blob, fileName);
                enqueueSnackbar(successMessage, { variant: 'success', autoHideDuration: 3000 });
            } else {
                enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 3000 });
            }
        } catch (e) {
            console.error((e as Error).message);
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 3000 });
        } finally {
            setExportLoading(false);
        }
    };

    return {
        exportLoading,
        handleExport,
    };
};

export default useExport;
